import {
    ChevronDownIcon,
    MenuIcon,
    ShoppingCartIcon,
} from '@heroicons/react/outline'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import { flushSync } from 'react-dom'
import useSWR from 'swr'
import { CartAPI, CartFecther } from '../../apis/CartAPI'
import { getMainProductsFromCart } from '../../helpers/checkout'
import { UserStore } from '../../state/UserStore'
import { ModalStore } from '../../state/ModalStore'
import LoginButton from '../LoginButton'
import HomepageBookCallLeadModal from '../Modals/HomepageBookCallLeadModal'
import BrandMark from './BrandMark'
import HomepageBookCallButton, {
    homepageButtonStyles,
} from './HomepageBookCallButton'
import { homepageNav } from './config'

const INDUSTRIES_MENU_SECTIONS = [
    {
        title: 'Home Services',
        items: [
            { label: 'Roofing', href: '/roofers' },
            { label: 'HVAC & Plumbing', href: '/hvac' },
            { label: 'Landscaping', href: '/landscaping' },
            { label: 'Pest Control', href: '/pest' },
            { label: 'Exterior Cleaning', href: '/exterior-cleaning' },
            { label: 'Residential Cleaning', href: '/cleaning' },
        ],
    },
    {
        title: 'Real Estate',
        items: [
            {
                label: 'Real Estate Investors',
                href: '/real-estate-investors',
            },
            { label: 'Real Estate Agents', href: '/real-estate-agents' },
            { label: 'Mortgage', href: '/mortgage' },
        ],
    },
    {
        title: 'Professional & Financial',
        items: [
            { label: 'Dental', href: '/dental' },
            { label: 'Insurance', href: '/insurance' },
            { label: 'Financial', href: '/financial' },
        ],
    },
    {
        title: 'Platforms & Agencies',
        items: [
            { label: 'Marketing Agencies', href: '/marketing-agencies' },
            { label: 'SaaS Platforms', href: '/saas-platforms' },
            { label: 'Ecommerce', href: '/ecommerce' },
        ],
    },
]

const INDUSTRIES_MENU_COLUMNS = [
    [INDUSTRIES_MENU_SECTIONS[0], INDUSTRIES_MENU_SECTIONS[1]],
    [INDUSTRIES_MENU_SECTIONS[2], INDUSTRIES_MENU_SECTIONS[3]],
]

const INDUSTRIES_EXPLORE_ALL = {
    label: 'Explore all industries',
    href: '/all-industry',
    globalHref: '/all-industry',
}

let suppressedDesktopDropdownTitle = null

const LinkWrapper = ({
    item,
    className,
    children,
    onClick,
    onPointerDown,
    onMouseEnter,
    hrefOverride,
}) => {
    const href = hrefOverride || item.globalHref || item.href

    if (item.action === 'bookCall') {
        return (
            <button
                type="button"
                className={className}
                onClick={onClick}
                onPointerDown={onPointerDown}
                onMouseEnter={onMouseEnter}>
                {children}
            </button>
        )
    }

    if (item.external) {
        return (
            <a
                href={href}
                target="_blank"
                rel="noreferrer"
                className={className}
                onClick={onClick}
                onPointerDown={onPointerDown}
                onMouseEnter={onMouseEnter}>
                {children}
            </a>
        )
    }

    if (href?.startsWith('#')) {
        return (
            <a
                href={href}
                className={className}
                onClick={onClick}
                onPointerDown={onPointerDown}
                onMouseEnter={onMouseEnter}>
                {children}
            </a>
        )
    }

    return (
        <Link href={href || '/'}>
            <a
                className={className}
                onClick={onClick}
                onPointerDown={onPointerDown}
                onMouseEnter={onMouseEnter}>
                {children}
            </a>
        </Link>
    )
}

function IndustryMenuItem({
    item,
    isHomepage,
    onBookCall,
    onClose,
    compact,
    highlighted,
}) {
    return (
        <LinkWrapper
            item={item}
            hrefOverride={isHomepage ? item.href : item.globalHref}
            onClick={(event) => {
                if (item.action === 'bookCall') {
                    event.preventDefault()
                    onBookCall?.()
                }
                onClose?.()
            }}
            className={`group flex items-center justify-between gap-4 transition ${
                highlighted
                    ? 'rounded-xl px-[11px] py-[9px] text-[14px] font-semibold text-[#ED5C2F] hover:bg-[#FDF2E4] hover:text-[#ED5C2F]'
                    : `rounded-xl font-semibold text-[#3F454F] hover:bg-[#FDF2E4] hover:text-[#1A1D23] ${
                          compact
                              ? 'px-3 py-[10px] text-[14px]'
                              : 'px-[11px] py-[9px] text-[14px]'
                      }`
            }`}>
            <span
                className={`font-semibold ${
                    compact ? 'leading-5' : 'leading-normal'
                }`}>
                {item.label}
            </span>
            {highlighted && (
                <svg
                    width="13"
                    height="13"
                    fill="none"
                    viewBox="0 0 16 16"
                    className="shrink-0 transition-transform group-hover:translate-x-0.5">
                    <path
                        d="M6 4l4 4-4 4"
                        stroke="currentColor"
                        strokeWidth="1.6"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
            )}
        </LinkWrapper>
    )
}

function IndustryMenuSection({
    section,
    isHomepage,
    onBookCall,
    onClose,
    compact,
    bordered,
}) {
    return (
        <div
            className={`${
                bordered ? 'mt-[14px] border-t border-black/[0.07] pt-3' : ''
            } ${compact ? 'space-y-1' : 'space-y-0'}`}>
            <div
                className={`font-bold uppercase text-[#7D8798] ${
                    compact
                        ? 'px-3 py-1.5 text-[10px] tracking-[0.14em]'
                        : 'px-2.5 py-1.5 text-[11px] tracking-[0.12em]'
                }`}>
                {section.title}
            </div>
            <div>
                {section.items.map((item) => (
                    <IndustryMenuItem
                        key={item.label}
                        item={item}
                        isHomepage={isHomepage}
                        onBookCall={onBookCall}
                        onClose={onClose}
                        compact={compact}
                    />
                ))}
            </div>
        </div>
    )
}

function IndustriesMegaMenu({ isHomepage, onBookCall, onClose }) {
    return (
        <div className="pointer-events-auto absolute left-0 top-[calc(100%+10px)] z-[100] flex w-[580px] flex-col rounded-[20px] border border-black/10 bg-white px-5 pb-2.5 pt-[18px] opacity-100 shadow-[0_20px_50px_-8px_rgba(0,0,0,0.14)] transition before:absolute before:inset-x-0 before:-top-[11px] before:h-3 before:content-['']">
            <div className="flex">
                {INDUSTRIES_MENU_COLUMNS.map((column, columnIndex) => (
                    <div
                        key={columnIndex}
                        className={`min-w-0 flex-1 pb-2 ${
                            columnIndex > 0
                                ? 'ml-7 border-l border-black/[0.06] pl-7'
                                : ''
                        }`}>
                        {column.map((section, sectionIndex) => (
                            <IndustryMenuSection
                                key={section.title}
                                section={section}
                                isHomepage={isHomepage}
                                onBookCall={onBookCall}
                                onClose={onClose}
                                bordered={sectionIndex > 0}
                            />
                        ))}
                    </div>
                ))}
            </div>
            <div className="mt-2.5 border-t border-black/[0.08] pt-2.5">
                <IndustryMenuItem
                    item={INDUSTRIES_EXPLORE_ALL}
                    isHomepage={isHomepage}
                    onBookCall={onBookCall}
                    onClose={onClose}
                    highlighted={true}
                />
            </div>
        </div>
    )
}

function IndustriesMobileMenu({ isHomepage, onBookCall, onClose }) {
    return (
        <div className="space-y-2 rounded-[14px] bg-[#FAF7F2] p-1.5">
            {INDUSTRIES_MENU_SECTIONS.map((section, index) => (
                <IndustryMenuSection
                    key={section.title}
                    section={section}
                    isHomepage={isHomepage}
                    onBookCall={onBookCall}
                    onClose={onClose}
                    compact={true}
                    bordered={index > 0}
                />
            ))}
            <div className="border-t border-black/10 pt-3">
                <IndustryMenuItem
                    item={INDUSTRIES_EXPLORE_ALL}
                    isHomepage={isHomepage}
                    onBookCall={onBookCall}
                    onClose={onClose}
                    compact={true}
                    highlighted={true}
                />
            </div>
        </div>
    )
}

function PlatformMegaMenu({
    group,
    isHomepage,
    onBookCall,
    onClose,
    mobile = false,
}) {
    const visibleItems = group.items.filter((item) => !item.hidden)
    const supportItems = visibleItems.filter((item) => item.support)
    const platformItems = visibleItems.filter((item) => !item.support)

    const itemClassName = mobile
        ? 'flex w-full items-center rounded-xl bg-white px-3 py-2.5 text-left transition hover:bg-[#FDF2E4]'
        : 'block rounded-xl px-[11px] py-[9px] text-[14px] font-semibold text-[#3F454F] transition hover:bg-[#FDF2E4] hover:text-[#1A1D23]'

    const renderItem = (item) => (
        <LinkWrapper
            key={item.label}
            item={item}
            hrefOverride={isHomepage ? item.href : item.globalHref}
            onClick={(event) => {
                if (item.action === 'bookCall') {
                    event.preventDefault()
                    onBookCall?.()
                }
                onClose?.()
            }}
            className={itemClassName}>
            <span
                className={
                    mobile
                        ? 'block text-sm font-medium text-[#3F454F]'
                        : 'block min-w-0'
                }>
                <span className="block leading-normal">{item.label}</span>
            </span>
        </LinkWrapper>
    )

    if (mobile) {
        return (
            <div className="space-y-3">
                <div className="space-y-1">
                    {platformItems.map((item) => renderItem(item))}
                </div>
                {supportItems.length > 0 && (
                    <div className="border-t border-black/10 pt-3">
                        <div className="px-3 pb-2 text-[10px] font-bold uppercase tracking-[0.16em] text-[#7D8798]">
                            Support
                        </div>
                        <div className="space-y-1">
                            {supportItems.map((item) => renderItem(item))}
                        </div>
                    </div>
                )}
            </div>
        )
    }

    return (
        <div className="pointer-events-auto absolute left-0 top-[calc(100%+10px)] z-[100] block w-[460px] rounded-[20px] border border-black/10 bg-white px-5 pb-2.5 pt-[18px] opacity-100 shadow-[0_20px_50px_-8px_rgba(0,0,0,0.14)] transition before:absolute before:inset-x-0 before:-top-[11px] before:h-3 before:content-['']">
            <div className="flex">
                <div className="min-w-0 flex-1 pb-2">
                    <div className="px-2.5 py-1.5 text-[11px] font-bold uppercase tracking-[0.12em] text-[#7D8798]">
                        {group.sectionTitle}
                    </div>
                    {platformItems.map((item) => renderItem(item))}
                </div>
                <div className="ml-7 min-w-0 flex-1 border-l border-black/10 pb-2 pl-7">
                    <div className="px-2.5 py-1.5 text-[11px] font-bold uppercase tracking-[0.12em] text-[#7D8798]">
                        Support
                    </div>
                    {supportItems.map((item) => renderItem(item))}
                </div>
            </div>
        </div>
    )
}

function DesktopNav({ isHomepage, hidden, onBookCall }) {
    const [activeDropdownTitle, setActiveDropdownTitle] = useState(null)
    const [activeSecondaryPane, setActiveSecondaryPane] = useState(null)
    const [closedDropdownTitle, setClosedDropdownTitle] = useState(
        suppressedDesktopDropdownTitle,
    )
    const orderedNav = [
        'Shop',
        'Services',
        'Platform',
        'Industries',
        'Resources',
    ]

    const closeDropdown = (title) => {
        suppressedDesktopDropdownTitle = title
        flushSync(() => {
            setActiveDropdownTitle(null)
            setActiveSecondaryPane(null)
            setClosedDropdownTitle(title)
        })
    }

    const openDropdown = (title) => {
        if (suppressedDesktopDropdownTitle === title) return
        setClosedDropdownTitle(null)
        setActiveDropdownTitle(title)
    }

    if (hidden) return null

    return (
        <ul className="hidden items-center justify-center gap-1 lg:flex">
            {homepageNav
                .filter((group) => !group.hidden)
                .sort(
                    (a, b) =>
                        orderedNav.indexOf(a.title) -
                        orderedNav.indexOf(b.title),
                )
                .map((group) => {
                    const isDropdownOpen =
                        activeDropdownTitle === group.title &&
                        closedDropdownTitle !== group.title

                    return (
                        <li
                            key={group.title}
                            className="relative"
                            onMouseEnter={() => openDropdown(group.title)}
                            onFocus={() => openDropdown(group.title)}
                            onMouseLeave={() => {
                                if (closedDropdownTitle === group.title) {
                                    suppressedDesktopDropdownTitle = null
                                    setClosedDropdownTitle(null)
                                }
                                setActiveDropdownTitle(null)
                                setActiveSecondaryPane(null)
                            }}
                            onBlur={(event) => {
                                if (
                                    event.currentTarget.contains(
                                        event.relatedTarget,
                                    )
                                ) {
                                    return
                                }
                                setActiveDropdownTitle(null)
                                setActiveSecondaryPane(null)
                            }}>
                            <LinkWrapper
                                item={{
                                    href:
                                        group.title === 'Services'
                                            ? '/services'
                                            : group.title === 'Platform'
                                            ? '/platform/features'
                                            : group.title === 'Shop'
                                            ? '/products'
                                            : undefined,
                                }}
                                onPointerDown={() => closeDropdown(group.title)}
                                onClick={() => closeDropdown(group.title)}
                                className={`flex items-center gap-[5px] rounded-[10px] px-2.5 py-2 text-[14px] font-medium transition hover:bg-black/[0.05] hover:text-[#1A1D23] ${
                                    isDropdownOpen
                                        ? 'bg-black/[0.05] text-[#1A1D23]'
                                        : 'text-[#3F454F]'
                                }`}>
                                <span>{group.title}</span>
                                <svg
                                    width="13"
                                    height="13"
                                    fill="none"
                                    viewBox="0 0 16 16"
                                    className={`transition ${
                                        isDropdownOpen ? 'rotate-180' : ''
                                    }`}>
                                    <path
                                        d="M4 6l4 4 4-4"
                                        stroke="currentColor"
                                        strokeWidth="1.6"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    />
                                </svg>
                            </LinkWrapper>
                            {!isDropdownOpen ? null : group.title === 'Shop' ? (
                                <div
                                    className={`pointer-events-auto absolute left-0 top-[calc(100%+10px)] z-[100] block rounded-[20px] border border-black/10 bg-white p-2.5 opacity-100 shadow-[0_20px_50px_-8px_rgba(0,0,0,0.14)] transition before:absolute before:inset-x-0 before:-top-[11px] before:h-3 before:content-[''] ${
                                        activeSecondaryPane
                                            ? 'w-[540px]'
                                            : 'w-[270px]'
                                    }`}>
                                    <div>
                                        <div
                                            className={`flex ${
                                                activeSecondaryPane
                                                    ? ''
                                                    : 'w-[250px]'
                                            }`}>
                                            <div
                                                className={`w-[250px] flex-shrink-0 ${
                                                    activeSecondaryPane
                                                        ? ''
                                                        : ''
                                                }`}>
                                                <div className="px-2.5 py-1.5 text-[11px] font-bold uppercase tracking-[0.12em] text-[#7D8798]">
                                                    {group.sectionTitle}
                                                </div>
                                                <div className="space-y-0">
                                                    {group.items
                                                        .filter(
                                                            (item) =>
                                                                !item.hidden,
                                                        )
                                                        .map((item) => {
                                                            const isSecondaryActive =
                                                                activeSecondaryPane ===
                                                                item.label

                                                            return (
                                                                <LinkWrapper
                                                                    key={
                                                                        item.label
                                                                    }
                                                                    item={item}
                                                                    hrefOverride={
                                                                        isHomepage
                                                                            ? item.href
                                                                            : item.globalHref
                                                                    }
                                                                    onClick={() => {
                                                                        closeDropdown(
                                                                            group.title,
                                                                        )
                                                                    }}
                                                                    onMouseEnter={() =>
                                                                        setActiveSecondaryPane(
                                                                            item.flyoutItems
                                                                                ? item.label
                                                                                : null,
                                                                        )
                                                                    }
                                                                    className={`block rounded-xl px-[11px] py-[9px] text-[14px] font-semibold text-[#3F454F] transition hover:bg-[#FDF2E4] hover:text-[#1A1D23] ${
                                                                        isSecondaryActive
                                                                            ? 'bg-[#FDF2E4] text-[#1A1D23]'
                                                                            : ''
                                                                    }`}>
                                                                    <span className="block min-w-0">
                                                                        <span className="grid grid-cols-[1fr_auto] items-center gap-3">
                                                                            <span className="min-w-0">
                                                                                <span className="block leading-normal">
                                                                                    {
                                                                                        item.label
                                                                                    }
                                                                                </span>
                                                                            </span>
                                                                            {item.flyoutItems && (
                                                                                <svg
                                                                                    width="14"
                                                                                    height="14"
                                                                                    fill="none"
                                                                                    viewBox="0 0 16 16"
                                                                                    className="my-auto flex-shrink-0 text-[#ED5C2F]">
                                                                                    <path
                                                                                        d="M6 3l4 5-4 5"
                                                                                        stroke="currentColor"
                                                                                        strokeWidth="1.8"
                                                                                        strokeLinecap="round"
                                                                                        strokeLinejoin="round"
                                                                                    />
                                                                                </svg>
                                                                            )}
                                                                        </span>
                                                                    </span>
                                                                </LinkWrapper>
                                                            )
                                                        })}
                                                </div>
                                            </div>

                                            {activeSecondaryPane && (
                                                <div className="ml-5 flex w-[250px] flex-col border-l border-black/[0.07] pl-5">
                                                    <div className="px-2.5 py-1.5 text-[11px] font-bold uppercase tracking-[0.12em] text-[#7D8798]">
                                                        {
                                                            group.items.find(
                                                                (item) =>
                                                                    item.label ===
                                                                    activeSecondaryPane,
                                                            )?.flyoutTitle
                                                        }
                                                    </div>
                                                    <div className="space-y-0">
                                                        {group.items
                                                            .find(
                                                                (item) =>
                                                                    item.label ===
                                                                    activeSecondaryPane,
                                                            )
                                                            ?.flyoutItems?.map(
                                                                (item) => (
                                                                    <LinkWrapper
                                                                        key={
                                                                            item.label
                                                                        }
                                                                        item={
                                                                            item
                                                                        }
                                                                        hrefOverride={
                                                                            item.href
                                                                        }
                                                                        onClick={() => {
                                                                            closeDropdown(
                                                                                group.title,
                                                                            )
                                                                        }}
                                                                        className="block rounded-xl px-[11px] py-[9px] text-[14px] font-semibold text-[#3F454F] transition hover:bg-[#FDF2E4] hover:text-[#1A1D23]">
                                                                        <span className="block min-w-0">
                                                                            <span className="grid grid-cols-[1fr_auto] items-center gap-3">
                                                                                <span className="min-w-0">
                                                                                    <span className="block leading-normal">
                                                                                        {
                                                                                            item.label
                                                                                        }
                                                                                    </span>
                                                                                </span>
                                                                                {/* <svg
                                                                                width="14"
                                                                                height="14"
                                                                                fill="none"
                                                                                viewBox="0 0 16 16"
                                                                                className="my-auto flex-shrink-0 text-[#3F454F]">
                                                                                <path
                                                                                    d="M6 3l4 5-4 5"
                                                                                    stroke="currentColor"
                                                                                    strokeWidth="1.8"
                                                                                    strokeLinecap="round"
                                                                                    strokeLinejoin="round"
                                                                                />
                                                                            </svg> */}
                                                                            </span>
                                                                        </span>
                                                                    </LinkWrapper>
                                                                ),
                                                            )}
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        <div className="mt-1 border-t border-black/[0.08] pt-1">
                                            <Link href="/products">
                                                <a
                                                    className="flex items-center justify-between gap-2 rounded-xl px-[11px] py-[9px] text-[14px] font-semibold text-[#ED5C2F] transition hover:bg-[#FDF2E4] hover:text-[#ED5C2F]"
                                                    onClick={() => {
                                                        closeDropdown(
                                                            group.title,
                                                        )
                                                    }}>
                                                    <span>Shop all</span>
                                                    <svg
                                                        width="13"
                                                        height="13"
                                                        fill="none"
                                                        viewBox="0 0 16 16">
                                                        <path
                                                            d="M3 8h10M9 4l4 4-4 4"
                                                            stroke="currentColor"
                                                            strokeWidth="1.6"
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                        />
                                                    </svg>
                                                </a>
                                            </Link>
                                        </div>
                                    </div>
                                </div>
                            ) : group.title === 'Industries' ? (
                                <IndustriesMegaMenu
                                    isHomepage={isHomepage}
                                    onBookCall={onBookCall}
                                    onClose={() => closeDropdown(group.title)}
                                />
                            ) : group.title === 'Platform' ? (
                                <PlatformMegaMenu
                                    group={group}
                                    isHomepage={isHomepage}
                                    onBookCall={onBookCall}
                                    onClose={() => closeDropdown(group.title)}
                                />
                            ) : (
                                <div
                                    className={`pointer-events-auto absolute left-0 top-[calc(100%+10px)] z-[100] block rounded-[20px] border border-black/10 bg-white p-2.5 opacity-100 shadow-[0_20px_50px_-8px_rgba(0,0,0,0.14)] transition before:absolute before:inset-x-0 before:-top-[11px] before:h-3 before:content-[''] ${
                                        group.title === 'Resources'
                                            ? 'w-[190px]'
                                            : 'w-[250px]'
                                    }`}>
                                    <div>
                                        {group.sectionTitle && (
                                            <div className="px-2.5 py-1.5 text-[11px] font-bold uppercase tracking-[0.12em] text-[#7D8798]">
                                                {group.sectionTitle}
                                            </div>
                                        )}
                                        {group.items
                                            .filter((item) => !item.hidden)
                                            .map((item) => (
                                                <div key={item.label}>
                                                    {item.separatorBefore && (
                                                        <div className="my-2 border-t border-black/10" />
                                                    )}
                                                    <LinkWrapper
                                                        item={item}
                                                        hrefOverride={
                                                            isHomepage
                                                                ? item.href
                                                                : item.globalHref
                                                        }
                                                        onClick={(event) => {
                                                            if (
                                                                item.action ===
                                                                'bookCall'
                                                            ) {
                                                                event.preventDefault()
                                                                onBookCall?.()
                                                            }
                                                            closeDropdown(
                                                                group.title,
                                                            )
                                                        }}
                                                        className="block rounded-xl px-[11px] py-[9px] text-[14px] font-semibold text-[#3F454F] transition hover:bg-[#FDF2E4] hover:text-[#1A1D23]">
                                                        <span className="block min-w-0">
                                                            <span className="block leading-normal">
                                                                {item.label}
                                                            </span>
                                                        </span>
                                                    </LinkWrapper>
                                                </div>
                                            ))}
                                        {group.title === 'Shop' && (
                                            <div className="mb-1 mt-2 border-t border-black/10 px-[11px] pb-1 pt-3">
                                                <Link href="/products">
                                                    <a
                                                        className="inline-flex items-center gap-2 text-[13px] font-semibold text-[#ED5C2F] transition hover:text-[#F28230]"
                                                        onClick={() => {
                                                            closeDropdown(
                                                                group.title,
                                                            )
                                                        }}>
                                                        <span>Shop all</span>
                                                        <svg
                                                            width="13"
                                                            height="13"
                                                            fill="none"
                                                            viewBox="0 0 16 16">
                                                            <path
                                                                d="M3 8h10M9 4l4 4-4 4"
                                                                stroke="currentColor"
                                                                strokeWidth="1.6"
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                            />
                                                        </svg>
                                                    </a>
                                                </Link>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </li>
                    )
                })}
        </ul>
    )
}

function MobileNav({
    isHomepage,
    isOpen,
    onClose,
    onBookCall,
    hidden,
    bookCallCtaLabel = 'Book a Call',
}) {
    const [expandedGroup, setExpandedGroup] = useState(null)

    useEffect(() => {
        if (!isOpen) setExpandedGroup(null)
    }, [isOpen])

    if (hidden || !isOpen) return null

    return (
        <div className="fixed inset-x-0 bottom-0 top-[69px] z-30 overflow-y-auto overscroll-contain border-t border-black/[0.06] bg-white lg:hidden">
            <div className="container mx-auto px-5 py-3 md:px-8">
                <div className="divide-y divide-black/[0.06] border-y border-black/[0.06]">
                    {homepageNav
                        .filter((group) => !group.hidden)
                        .map((group) => {
                            const isExpanded = expandedGroup === group.title
                            return (
                                <div
                                    key={group.title}
                                    className="overflow-hidden bg-white">
                                    <button
                                        type="button"
                                        aria-expanded={isExpanded}
                                        onClick={() =>
                                            setExpandedGroup(
                                                isExpanded ? null : group.title,
                                            )
                                        }
                                        className="flex w-full items-center justify-between px-0 py-[13px] text-left text-[15px] font-semibold text-[#3F454F] transition hover:text-[#1A1D23]">
                                        <span className="leading-5">
                                            {group.title}
                                        </span>
                                        <ChevronDownIcon
                                            className={`h-4 w-4 text-[#6F7785] transition-transform ${
                                                isExpanded ? 'rotate-180' : ''
                                            }`}
                                        />
                                    </button>
                                    {isExpanded && (
                                        <div className="pb-3">
                                            {group.title === 'Industries' ? (
                                                <IndustriesMobileMenu
                                                    isHomepage={isHomepage}
                                                    onBookCall={onBookCall}
                                                    onClose={onClose}
                                                />
                                            ) : group.title === 'Platform' ? (
                                                <PlatformMegaMenu
                                                    group={group}
                                                    isHomepage={isHomepage}
                                                    onBookCall={onBookCall}
                                                    onClose={onClose}
                                                    mobile={true}
                                                />
                                            ) : (
                                                <div className="space-y-0 rounded-[14px] bg-[#FAF7F2] p-1.5">
                                                    {group.items
                                                        .filter(
                                                            (item) =>
                                                                !item.hidden,
                                                        )
                                                        .map((item) => (
                                                            <div
                                                                key={
                                                                    item.label
                                                                }>
                                                                {item.separatorBefore && (
                                                                    <div className="my-2 border-t border-black/10" />
                                                                )}
                                                                <LinkWrapper
                                                                    item={item}
                                                                    hrefOverride={
                                                                        isHomepage
                                                                            ? item.href
                                                                            : item.globalHref
                                                                    }
                                                                    onClick={(
                                                                        event,
                                                                    ) => {
                                                                        if (
                                                                            item.action ===
                                                                            'bookCall'
                                                                        ) {
                                                                            event.preventDefault()
                                                                            onBookCall()
                                                                        }
                                                                        onClose()
                                                                    }}
                                                                    className="flex w-full items-center rounded-xl px-3 py-[10px] text-left transition hover:bg-[#FDF2E4]">
                                                                    <span className="block text-[14px] font-semibold leading-5 text-[#3F454F]">
                                                                        {
                                                                            item.label
                                                                        }
                                                                    </span>
                                                                </LinkWrapper>
                                                            </div>
                                                        ))}
                                                    {group.title === 'Shop' && (
                                                        <div className="mb-1 mt-2 border-t border-black/10 px-3 pb-1 pt-3">
                                                            <Link href="/products">
                                                                <a
                                                                    className="inline-flex items-center gap-2 text-[14px] font-semibold text-[#ED5C2F] transition hover:text-[#F28230]"
                                                                    onClick={
                                                                        onClose
                                                                    }>
                                                                    <span>
                                                                        Shop all
                                                                    </span>
                                                                    <svg
                                                                        width="13"
                                                                        height="13"
                                                                        fill="none"
                                                                        viewBox="0 0 16 16">
                                                                        <path
                                                                            d="M3 8h10M9 4l4 4-4 4"
                                                                            stroke="currentColor"
                                                                            strokeWidth="1.6"
                                                                            strokeLinecap="round"
                                                                            strokeLinejoin="round"
                                                                        />
                                                                    </svg>
                                                                </a>
                                                            </Link>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )
                        })}
                </div>
                <div className="mt-3">
                    <button
                        type="button"
                        onClick={onBookCall}
                        className={`${homepageButtonStyles.primary} w-full`.trim()}>
                        {bookCallCtaLabel}
                    </button>
                </div>
            </div>
        </div>
    )
}

function HomepageHeader({
    hideCartButton = false,
    hideLoginButton = false,
    hideNavigation = false,
    disableLogoLink = false,
    bookCallCtaLabel = 'Book a Call',
    bookCallModalProps,
}) {
    const router = useRouter()
    const isHomepage = router.pathname === '/'
    const user = UserStore((state) => state.user)
    const addModal = ModalStore((state) => state.add)
    const [isOpen, setIsOpen] = useState(false)
    const [isMounted, setIsMounted] = useState(false)
    const [cartCount, setCartCount] = useState(0)
    const { data: cart } = useSWR(CartAPI.getCart, CartFecther, {
        revalidateOnFocus: true,
    })

    useEffect(() => {
        setIsMounted(true)
    }, [])

    useEffect(() => {
        setIsOpen(false)
    }, [router.asPath])

    useEffect(() => {
        if (typeof document === 'undefined') return
        const original = document.body.style.overflow
        document.body.style.overflow = isOpen ? 'hidden' : original || ''
        return () => {
            document.body.style.overflow = original || ''
        }
    }, [isOpen])

    useEffect(() => {
        const count =
            cart?.items?.length > 0 ? getMainProductsFromCart(cart).length : 0
        setCartCount(count)
    }, [cart, router.asPath])

    const handleBookCall = () => {
        addModal(<HomepageBookCallLeadModal {...bookCallModalProps} />)
    }

    return (
        <>
            <header className="sticky top-0 z-50 border-b border-black/[0.06] bg-white">
                <div className="container mx-auto flex min-h-[68px] items-center justify-between gap-4 px-5 py-3 lg:grid lg:min-h-0 lg:grid-cols-[1fr_auto_1fr] lg:gap-7 lg:px-0 lg:py-3.5">
                    {disableLogoLink ? (
                        <div className="justify-self-start">
                            <BrandMark />
                        </div>
                    ) : (
                        <Link href="/">
                            <a className="justify-self-start">
                                <BrandMark />
                            </a>
                        </Link>
                    )}

                    <DesktopNav
                        isHomepage={isHomepage}
                        hidden={hideNavigation}
                        onBookCall={handleBookCall}
                    />

                    <div className="flex items-center justify-self-end gap-3 lg:gap-[18px]">
                        {!hideLoginButton &&
                            (isMounted && user?.ID ? (
                                <div className="hidden text-[#4A5160] transition hover:text-[#3F454F] lg:block">
                                    <LoginButton user={user} />
                                </div>
                            ) : (
                                <Link href="/login">
                                    <a className="hidden whitespace-nowrap text-[14px] font-medium text-[#4A5160] transition hover:text-[#1A1D23] lg:block">
                                        Log in
                                    </a>
                                </Link>
                            ))}

                        {!hideCartButton && (
                            <Link href="/cart">
                                <a
                                    title="Cart"
                                    className="relative flex h-8 w-8 items-center justify-center rounded-lg border border-black/10 text-[#6F7785] transition hover:border-[rgba(63,69,79,0.25)] hover:text-[#3F454F]">
                                    <ShoppingCartIcon className="h-[15px] w-[15px]" />
                                    {cartCount > 0 && (
                                        <span className="absolute -right-1.5 -top-1.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-[#ED5C2F] px-1 text-[10px] text-white">
                                            {cartCount}
                                        </span>
                                    )}
                                </a>
                            </Link>
                        )}

                        <div className="hidden lg:block">
                            <HomepageBookCallButton
                                className="px-[18px] py-[9px] text-[13px]"
                                showArrow={false}
                                modalProps={bookCallModalProps}>
                                {bookCallCtaLabel}
                            </HomepageBookCallButton>
                        </div>

                        {!hideNavigation && (
                            <button
                                type="button"
                                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-black/10 text-[#3F454F] lg:hidden"
                                onClick={() => setIsOpen((value) => !value)}
                                aria-label={
                                    isOpen
                                        ? 'Close navigation'
                                        : 'Open navigation'
                                }>
                                {isOpen ? (
                                    <svg
                                        width="18"
                                        height="18"
                                        viewBox="0 0 18 18"
                                        fill="none"
                                        aria-hidden="true"
                                        className="overflow-visible">
                                        <path
                                            d="M5 5l8 8M13 5l-8 8"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                        />
                                    </svg>
                                ) : (
                                    <MenuIcon className="h-5 w-5" />
                                )}
                            </button>
                        )}
                    </div>
                </div>
            </header>
            <MobileNav
                isHomepage={isHomepage}
                isOpen={isOpen}
                onClose={() => setIsOpen(false)}
                onBookCall={() => {
                    setIsOpen(false)
                    handleBookCall()
                }}
                hidden={hideNavigation}
                bookCallCtaLabel={bookCallCtaLabel}
            />
        </>
    )
}

export default HomepageHeader
